import React, { useState, useCallback, useEffect } from 'react'
import { DatePicker, DatePickerProps, Select } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { IconWrap, InputCommon, PageHeader, SortTitle } from '../../components'
import operationLogHeader from '../../assets/images/operationLogHeader.png'
import clsx from 'clsx'
import styles from './index.module.scss'
import { LogIcon, SearchIcon } from '../../assets/icons'
import { DataEmpty } from '../../components/DataEmpty'
import { Loading } from '../../components/Loading'
import { IOperationLog, IOpreationLogParams } from '../../models'
import debounce from 'lodash.debounce'
import { PaginationCommon } from '../../components/Pagination'
import moment from 'moment'
import { useGetOperationLog } from '../../hooks/query/operationLog'
import { OPERATION_TYPE_KEY, PLATFORM_KEY } from '../../until'
import { useTranslation } from 'react-i18next'
import CustomTable from '../../components/Table'


export const OperationLog = () => {
  const [params, setParams] = useState<IOpreationLogParams>({
    page: 1,
    page_size: 100,
  })

  const [typeOperationSelected, setSelectedTypeSelected] = useState<number | undefined>(undefined)
  const [platfromsSelected, setPlatfromsSelected] = useState<number | undefined>(undefined)
  const { data, isLoading, isFetching, refetch } = useGetOperationLog(params)
  const { t, i18n } = useTranslation()

  useEffect(() => {
    refetch()
  }, [i18n.language, refetch])

  const typeOperation: { name: string; value: number }[] = Object.keys(OPERATION_TYPE_KEY).map((item) => {
    const operationKey = OPERATION_TYPE_KEY[Number(item)]

    return {
      name: t(`operation.${operationKey}`),
      value: Number(item),
    }
  })

  const platfroms: { label: string; value: number }[] = Object.keys(PLATFORM_KEY).map((item) => (
    {
      label: t(`operation.${PLATFORM_KEY[Number(item)]}`),
      value: Number(item),
    }
  ))

  const onSort = (name: string) => {
    if (params.sort_by !== name) {
      setParams({ ...params, sort_by: name, sort_type: 'asc' })
    } else {
      params.sort_type === 'asc'
        ? setParams({ ...params, sort_type: 'desc' })
        : setParams({ ...params, sort_by: '', sort_type: 'desc' })
    }
  }

  const columns: ColumnsType<IOperationLog> = [
    {
      title: (
        <SortTitle
          title={t('operation.date')}
          value={params.sort_by === 'operated_at' ? params.sort_type : null}
          onSort={() => onSort('operated_at')}
        />
      ),
      dataIndex: 'operated_at',
      key: 'operated_at',
      width: '16%',
      render: (text) => text ? moment(text).format('YYYY/MM/DD HH:mm:ss') : '',
    },
    {
      title: (
        <SortTitle
          title={t('operation.username')}
          value={params.sort_by === 'username' ? params.sort_type : null}
          onSort={() => onSort('username')}
        />
      ),
      dataIndex: 'username',
      key: 'username',
      width: '14%',
    },
    {
      title: (
        <SortTitle
          title={t('operation.category')}
          value={params.sort_by === 'operation_type' ? params.sort_type : null}
          onSort={() => onSort('operation_type')}
        />
      ),
      dataIndex: 'operation_type',
      key: 'operation_type',
      width: '10%',
      render: (text) => t(`operation.${OPERATION_TYPE_KEY[text]}`),
    },
    {
      title: (
        <SortTitle
          title={t('operation.operationContent')}
          value={params.sort_by === 'api_name' ? params.sort_type : null}
          onSort={() => onSort('api_name')}
        />
      ),
      dataIndex: 'api_name',
      key: 'api_name',
      width: '12%',
    },
    {
      title: (
        <SortTitle
          title={t('operation.name')}
          value={params.sort_by === 'name' ? params.sort_type : null}
          onSort={() => onSort('name')}
        />
      ),
      dataIndex: 'name',
      key: 'name',
      width: '10%',
    },
    {
      title: (
        <SortTitle
          title={t('operation.logContent')}
          value={params.sort_by === 'value' ? params.sort_type : null}
          onSort={() => onSort('value')}
        />
      ),
      dataIndex: 'value',
      key: 'value',
      width: '10%',
    },
    {
      title: (
        <SortTitle
          title={t('operation.platform')}
          value={params.sort_by === 'platform' ? params.sort_type : null}
          onSort={() => onSort('platform')}
        />
      ),
      dataIndex: 'platform',
      key: 'platform',
      width: '8%',
      render: (value) => t(`operation.${PLATFORM_KEY[value]}`)
    },
    {
      title: (
        <SortTitle
          title={t('operation.version')}
          value={params.sort_by === 'version' ? params.sort_type : null}
          onSort={() => onSort('version')}
        />
      ),
      dataIndex: 'version',
      key: 'version',
      width: '11%',
    },
    {
      title: (
        <SortTitle
          title={t('operation.device')}
          value={params.sort_by === 'device' ? params.sort_type : null}
          onSort={() => onSort('device')}
        />
      ),
      dataIndex: 'device',
      key: 'device',
      width: '11%',
    },
  ]

  const onChangePagination = (page: number, page_size: number) => {
    if (page_size !== params.page_size) {
      return setParams({ ...params, page: 1, page_size })
    }
    setParams({ ...params, page, page_size })
  }

  const handleChangeSelectType = (value: number) => {
    setSelectedTypeSelected(value)
    setParams({ ...params, page: 1, operation_type: value })
  }

  const handleChangeSelectPlatfrom = (value: number) => {
    setPlatfromsSelected(value)
    setParams({ ...params, page: 1, platform: value })
  }

  const onHandleSearch = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    debouncedSearch(e.target.value)
  }

  const debouncedSearch = useCallback(
    debounce((nextValue) => setParams((prev: IOpreationLogParams) => ({ ...prev, text_search: nextValue, page: 1 })), 500),
    [],
  )

  const handleChangeDateStart: DatePickerProps['onChange'] = (date) => {
    setParams({ ...params, operated_at_from: date ? moment(date).format('YYYY/MM/DD') : null, page: 1 })
  }
  const handleChangeDateEnd: DatePickerProps['onChange'] = (date) => {
    setParams({ ...params, operated_at_to: date ? moment(date).format('YYYY/MM/DD') : null, page: 1 })
  }

  return (
    <div className={clsx(styles.operation)}>
      <PageHeader title={t('operation.operationLogList')} backgroundImage={operationLogHeader} height={'108px'}>
        <div className={styles.operation__header}>
          <InputCommon
            suffix={<IconWrap icon={<SearchIcon />} />}
            placeholder={t('operation.search')}
            className={styles.operation__header__search__input}
            onChange={onHandleSearch}
          />
        </div>
      </PageHeader>
      <Loading isLoading={isLoading || isFetching} />
      <div className={clsx(styles.operation__content__filter)}>
        <div
          className={clsx(
            styles.operation__content__filter__group,
            {
              [styles.hidden]: data?.data.length === 0 || isLoading || isFetching,
            },
            'select__group',
          )}
        >
          <Select
            style={{ width: 'fit-content' }}
            className={styles.operation__content__filter__group__select}
            allowClear
            value={typeOperationSelected}
            optionLabelProp="label"
            placeholder={
              <>
                <IconWrap color="#8C8C8C" icon={<LogIcon />} className={clsx(styles.icon__filter, styles.stafficon)} />
                {t('operation.category')}
              </>
            }
            onChange={handleChangeSelectType}
          >
            {typeOperation.map((item: { name: string; value: number }) => (
              <Select.Option
                key={item.value}
                value={item.value}
                className="select__option"
                label={
                  <React.Fragment>
                    <IconWrap color="#8C8C8C" icon={<LogIcon />} className={clsx(styles.icon__filter, styles.stafficon)} />
                    {item.name}
                  </React.Fragment>
                }
              >
                <span className={styles.text}>{item.name}</span>
              </Select.Option>
            ))}
          </Select>
          <Select
            style={{ width: 'fit-content' }}
            className={styles.operation__content__filter__group__select}
            allowClear
            value={platfromsSelected}
            optionLabelProp="label"
            placeholder={t('operation.platform')}
            onChange={handleChangeSelectPlatfrom}
            options={platfroms}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DatePicker
              onChange={handleChangeDateStart}
              className={styles.operation__content__filter__date}
              placeholder={t('operation.dateTime')}
              format='YYYY/MM/DD'
            />
            <div style={{ width: '15px', height: '2px', background: '#747373' }}></div>
            <DatePicker
              onChange={handleChangeDateEnd}
              className={styles.operation__content__filter__date}
              placeholder={t('operation.dateTime')}
              format='YYYY/MM/DD'
            />
          </div>
        </div>
      </div>
      {!isLoading && (
        <div className={clsx(styles.operation__content)}>
          {data?.data && data.data.length > 0 ? (
            <>
              <CustomTable rowKey="id" columns={columns} dataSource={data.data} pagination={false} scroll={{ x: 700, y: 'calc(100vh - 401px)' }} />

              <div className={styles.pagination}>
                <PaginationCommon
                  defaultCurrent={1}
                  total={data.pagination.total}
                  current={data.pagination.current_page}
                  pageSize={data.pagination.per_page}
                  onChangePagination={onChangePagination}
                  showSizeChanger
                  pageSizeOptions={['10', '20', '50', '100']}
                />
              </div>
            </>
          ) : (
            <DataEmpty className={styles.empty__results}>
              <></>
            </DataEmpty>
          )}
        </div>
      )}
    </div>
  )
}
